import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { useAppStore } from '../../../shared/hooks/useAppStore';
import { useLoyaltyStore } from '../../loyalty/hooks';
import { getPredictions, PREDICTION_QUERY_KEYS } from '../api/prediction.api';

/**
 * SWRN-1033 — post-purchase "prediction" (fortune) shown on the completed
 * screen instead of advertising.
 *
 * Predictions are loaded once from the static list at /predictions/uk.json
 * (see scripts/build-predictions.mjs). This hook is the single data-source
 * "seam" — the visual components never touch the source directly.
 *
 * FALLBACK_PREDICTIONS is used only while the list is loading or if the fetch
 * fails, so the reveal always has text to show.
 */
const PREDICTION_LANG = 'uk';

const FALLBACK_PREDICTIONS = [
    'Ваші плани мають звичку здійснюватися. Особливо грандіозні.',
    'Скоро ви отримаєте знак, на який так довго чекали.',
    'Сьогодні — ідеальний день, щоб почати щось.',
    'Ваша енергія зараз працює краще за будь-який павербанк.',
    'Наступний тиждень готує для вас приємну несподіванку.',
    'Ви на правильному шляху, навіть якщо навігатор каже інакше.',
    'Фортуна вже підморгує вам. Підморгніть їй у відповідь!',
    'Ваші зусилля конвертуються в успіх за дуже вигідним курсом.',
    'Будьте готові: скоро доведеться купувати більший гаманець.',
] as const;

interface UsePredictionResult {
    /** AC: hide the prediction entirely when the loyalty program is active. */
    shouldShow: boolean;
    text: string;
}

const CURRENCIES = {
    UAH: "UAH",
} as const;

const AVAILABLE_CURRENCIES_FOR_PREDICTION: string[] = [CURRENCIES.UAH]

const isUACurrency = (currency: string) => {
    return AVAILABLE_CURRENCIES_FOR_PREDICTION.includes(currency);
}

export const usePrediction = (): UsePredictionResult => {
    const currencyCode = useAppStore(store => store.vm.currency_code);
    const isLoyaltyProgramActive = useLoyaltyStore((s) =>
        s.isLoyaltyProgramActive(),
    );
    
    const shouldShow = !isLoyaltyProgramActive && isUACurrency(currencyCode);

    const { data } = useQuery({
        queryKey: PREDICTION_QUERY_KEYS.lang(PREDICTION_LANG),
        queryFn: () => getPredictions(PREDICTION_LANG),
        staleTime: Infinity,
        gcTime: Infinity,
        // Don't fetch the list when the prediction is hidden anyway.
        enabled: shouldShow,
    });

    // Pick once; React Query returns a stable `data` reference, so the choice
    // stays put across re-renders.
    const text = useMemo(() => {
        const list = data?.length ? data : FALLBACK_PREDICTIONS;
        return list[Math.floor(Math.random() * list.length)];
    }, [data]);

    return { shouldShow, text };
};
